import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2, Mic, ExternalLink, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/ui/form";
import { Input } from "@/ui/input";

// GTM DataLayer type declaration
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

const formSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    email: z.string().email("Please enter a valid email address"),
    hasWebsite: z.boolean().default(true),
    website: z.string().optional(),
    proposedAgentName: z.string().min(1, "Agent name is required"),
  })
  .refine(
    (data) => {
      if (
        data.hasWebsite &&
        (!data.website ||
          data.website.trim() === "" ||
          data.website.trim() === "https://")
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Please enter a valid website URL",
      path: ["website"],
    }
  );

type FormData = z.infer<typeof formSchema>;

interface AgentData {
  id: string;
  assistant?: {
    id: string;
    name: string;
  };
  failedToScrape?: boolean;
  [key: string]: any;
}

export interface AIVoiceAgentFormProps {
  trigger?: React.ReactNode;
  className?: string;
  buttonText?: string;
  onSuccess?: (data: AgentData) => void;
  onError?: (error: string) => void;
  enableGTMTracking?: boolean;
}

export function AIVoiceAgentForm({
  trigger,
  className,
  buttonText = "Create Free AI Voice Agent",
  onSuccess,
  onError,
  enableGTMTracking = false,
}: AIVoiceAgentFormProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [agentData, setAgentData] = React.useState<AgentData | null>(null);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      email: "",
      hasWebsite: true,
      website: "https://",
      proposedAgentName: "",
    },
  });

  const hasWebsite = form.watch("hasWebsite");

  const handleRemoveWebsite = () => {
    form.setValue("hasWebsite", false);
    form.setValue("website", "");
  };

  const handleAddWebsite = () => {
    form.setValue("hasWebsite", true);
    form.setValue("website", "https://");
  };

  const handleWebsiteChange = (value: string) => {
    if (
      value &&
      !value.startsWith("http://") &&
      !value.startsWith("https://")
    ) {
      form.setValue("website", `https://${value}`);
    } else {
      form.setValue("website", value);
    }
  };

  const onSubmit = async (values: FormData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append("username", values.firstName);
      formData.append("email", values.email);
      formData.append("name", values.proposedAgentName);

      if (typeof window !== "undefined") {
        const utmMedium = new URLSearchParams(window.location.search).get(
          "utm_medium"
        );
        if (utmMedium) {
          formData.append("medium", utmMedium);
        }
        formData.append("source", window.location.href);
      }

      // Send website URL or placeholder if no website
      const websiteUrl =
        values.hasWebsite && values.website
          ? values.website
          : "https://placeholder-no-website.com";
      formData.append("url", websiteUrl);
      formData.append("image", "potential-default-voice.png");

      const response = await fetch(
        `https://api.potential.com/api/vapi/simpleassistant`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAgentData(data);
      setIsSuccess(true);

      // Call success callback
      onSuccess?.(data);

      // GTM tracking for successful voice agent creation
      if (
        enableGTMTracking &&
        typeof window !== "undefined" &&
        window.dataLayer
      ) {
        window.dataLayer.push({
          event: "voicebot_creation_success",
          form_type: "voicebot",
          agent_name: values.proposedAgentName,
          has_website: values.hasWebsite,
          user_email: values.email,
          agent_id: data?.assistant?.id,
        });
      }
    } catch (error) {
      console.error("Error creating AI voice agent:", error);
      const errorMessage =
        "Sorry, we couldn't create your agent right now. Please try again later.";
      setError(errorMessage);

      // Call error callback
      onError?.(errorMessage);

      // GTM tracking for voice agent creation failure
      if (
        enableGTMTracking &&
        typeof window !== "undefined" &&
        window.dataLayer
      ) {
        window.dataLayer.push({
          event: "voicebot_creation_failure",
          form_type: "voicebot",
          agent_name: values.proposedAgentName,
          has_website: values.hasWebsite,
          user_email: values.email,
          error_message:
            error instanceof Error ? error.message : "Unknown error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    form.reset({
      firstName: "",
      email: "",
      hasWebsite: true,
      website: "https://",
      proposedAgentName: "",
    });
    setAgentData(null);
    setIsSuccess(false);
    setError(null);
    setIsOpen(false);
  };

  const handleTestAgent = () => {
    if (agentData?.assistant?.id) {
      window.open(
        `https://ai.potential.com/voice/${agentData.assistant.id}`,
        "_blank"
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className={className}>
            <Mic className="mr-2 h-4 w-4" />
            {buttonText}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5 text-foreground" />
            {isSuccess
              ? "Your AI Voice Agent is Ready!"
              : "Create Your Free AI Voice Agent"}
          </DialogTitle>
          <DialogDescription>
            {isSuccess
              ? "Your AI voice agent has been successfully created. You can now test it!"
              : "Fill out the form below to create your personalized AI voice agent in seconds."}
          </DialogDescription>
        </DialogHeader>

        {!isSuccess ? (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 gtm-voicebot-form"
            >
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your first name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter your email address"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {hasWebsite ? (
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center justify-between">
                        Website URL
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveWebsite}
                          className="h-auto p-1 text-xs text-muted-foreground gtm-voicebot-form-remove-website"
                        >
                          <X className="h-3 w-3 mr-1 text-muted-foreground" />I
                          don't have a website
                        </Button>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://your-website.com"
                          {...field}
                          onChange={(e) => {
                            handleWebsiteChange(e.target.value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Website</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleAddWebsite}
                      className="h-auto p-1 text-xs text-primary hover:text-primary gtm-voicebot-form-add-website"
                    >
                      + Add website
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    No website required
                  </p>
                </div>
              )}

              <FormField
                control={form.control}
                name="proposedAgentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proposed Agent Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter a name for your AI agent"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-red-800 dark:text-red-200 text-sm">
                    {error}
                  </p>
                </div>
              )}

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full gtm-voicebot-form-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Voice Agent"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        ) : (
          <div className="space-y-4 text-center">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-green-800 dark:text-green-200">
                <h3 className="font-semibold">Agent Created Successfully!</h3>
                <p className="text-sm mt-1">
                  Agent Name:{" "}
                  {agentData?.assistant?.name ||
                    form.getValues("proposedAgentName")}
                </p>
                {agentData?.failedToScrape && (
                  <p className="text-xs mt-2 text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded border border-yellow-200 dark:border-yellow-800">
                    We couldn't scrape your website, so we're using default
                    information. You'll receive an email with login details to
                    access your dashboard and train your voice agent with your
                    specific content.
                  </p>
                )}
              </div>
            </div>

            <Button
              onClick={handleTestAgent}
              disabled={!agentData?.assistant?.id}
              className="w-full gtm-voicebot-form-test-agent"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Talk to your AI Agent Now ➡
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
